/**
 * syncService.js
 * Synchronisation bidirectionnelle Local ↔ Serveur
 *
 * Stratégie : LOCAL GAGNE (écrase serveur en cas de conflit)
 * Direction :
 *   Upload : sync_queue → serveur (tout ce qui a été créé/modifié offline)
 *   Download : serveur → IndexedDB (produits, clients, config)
 */

import {
  dbPutMany, dbPut, dbGetAll, dbClear, getPendingQueue,
  updateQueueItem, clearDoneQueue, metaGet, metaSet,
} from '../db/localDB';
import { saveSubscriptionToken } from './subscriptionService';

const API = process.env.REACT_APP_API_URL || 'https://api.easyfacture.easyclick-solution.fr';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: authHeaders(),
    ...opts,
  });
  if (!r.ok) throw await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
  return r.json();
}

// ─── MAP entité → endpoint API ─────────────────────────────────────────────
const ENTITY_ENDPOINTS = {
  invoices:  '/invoices',
  quotes:    '/quotes',
  clients:   '/clients',
  products:  '/products',
  pos_sales: '/pos',
  credits:   '/client-credits',
};

// ─── UPLOAD : envoi des opérations offline vers le serveur ─────────────────
export async function uploadPendingSync(onProgress) {
  const queue = await getPendingQueue();
  if (queue.length === 0) return { uploaded: 0, errors: 0 };

  let uploaded = 0;
  let errors = 0;

  for (const item of queue) {
    const endpoint = ENTITY_ENDPOINTS[item.entity];
    if (!endpoint) {
      await updateQueueItem(item.queueId, { status: 'error', error: 'Unknown entity' });
      errors++;
      continue;
    }

    try {
      await updateQueueItem(item.queueId, { status: 'syncing' });

      let method, url, body;
      const isLocal = String(item.localId || '').startsWith('local_');

      if (item.operation === 'create' || (item.operation === 'update' && isLocal)) {
        // Nouvelle entité créée offline → POST
        method = 'POST';
        url = endpoint;
        body = { ...item.data };
        // Supprimer l'ID local pour que le serveur génère un vrai ID
        delete body.id;
        delete body._localId;
        delete body._offline;
      } else if (item.operation === 'update') {
        method = 'PUT';
        url = `${endpoint}/${item.data.id}`;
        body = item.data;
      } else if (item.operation === 'delete') {
        method = 'DELETE';
        url = `${endpoint}/${item.data.id}`;
        body = null;
      }

      const result = await apiFetch(url, {
        method,
        body: body ? JSON.stringify(body) : undefined,
      });

      // Si create → remplacer l'entité locale avec le vrai ID serveur
      if ((item.operation === 'create' || isLocal) && result?.id) {
        const store = item.entity;
        // Supprimer l'ancienne entrée avec ID local
        const all = await dbGetAll(store);
        const old = all.find(e => e.id === item.localId || e._localId === item.localId);
        if (old) {
          const { id: _oldId, _localId: _l, _offline: _o, ...rest } = old;
          await dbPut(store, { ...rest, id: result.id, _synced: true });
          // Supprimer l'ancienne (si clé différente)
          if (old.id !== result.id) {
            // On ne peut pas facilement supprimer par ancienne clé ici,
            // mais la nouvelle entrée est correcte
          }
        }
      }

      await updateQueueItem(item.queueId, { status: 'done' });
      uploaded++;

      onProgress?.({ uploaded, total: queue.length, entity: item.entity });

    } catch (err) {
      await updateQueueItem(item.queueId, {
        status: 'error',
        error: err.error || err.message || 'Sync error',
        retries: (item.retries || 0) + 1,
      });
      errors++;
    }
  }

  await clearDoneQueue();
  return { uploaded, errors };
}

// ─── DOWNLOAD : récupération des données serveur → local ───────────────────
export async function downloadFromServer(onProgress) {
  const results = {};

  const downloads = [
    { key: 'products',  url: '/products?limit=9999' },
    { key: 'clients',   url: '/clients?limit=9999' },
    { key: 'invoices',  url: '/invoices?limit=9999' },
    { key: 'quotes',    url: '/quotes?limit=9999' },
    { key: 'pos_sales', url: '/pos?limit=9999' },   // ✅ Corrigé : était '/pos/sales?limit=9999' (404)
  ];

  for (let i = 0; i < downloads.length; i++) {
    const { key, url } = downloads[i];
    try {
      const res = await apiFetch(url);
      const data = res.data || res;
      if (Array.isArray(data)) {
        await dbClear(key);
        await dbPutMany(key, data.map(item => ({ ...item, _synced: true })));
        results[key] = data.length;
      }
      onProgress?.({ step: key, current: i + 1, total: downloads.length });
    } catch (err) {
      console.warn(`[Sync] Download ${key} failed:`, err);
      results[key] = 0;
    }
  }

  return results;
}

// ─── SYNC COMPLET ──────────────────────────────────────────────────────────
export async function fullSync(onProgress) {
  const startedAt = new Date().toISOString();

  onProgress?.({ phase: 'upload', message: 'Envoi des données locales…' });
  const upload = await uploadPendingSync(onProgress);

  onProgress?.({ phase: 'download', message: 'Téléchargement des données serveur…' });
  const download = await downloadFromServer(onProgress);

  // Récupérer et sauvegarder le token d'abonnement
  try {
    const subResult = await apiFetch('/auth/subscription-check');
    if (subResult.subscriptionToken) {
      await saveSubscriptionToken(subResult.subscriptionToken);
    }
  } catch { /* ignore */ }

  await metaSet('lastFullSync', startedAt);
  await metaSet('lastSyncResult', { upload, download, at: startedAt });

  return { upload, download, syncedAt: startedAt };
}

// ─── SYNC LÉGER (juste upload queue) ──────────────────────────────────────
export async function quickSync(onProgress) {
  const queue = await getPendingQueue();
  if (queue.length === 0) return { uploaded: 0, errors: 0 };
  return uploadPendingSync(onProgress);
}

// ─── Infos de la dernière sync ─────────────────────────────────────────────
export async function getLastSyncInfo() {
  const lastSync = await metaGet('lastFullSync');
  const result   = await metaGet('lastSyncResult');
  return { lastSync, result };
}